import {
  CloseCircleFilled,
  DeleteOutlined,
  EditOutlined,
  StarFilled,
  StarOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { Popconfirm, Tooltip, message } from "antd";
import type { TableColumnsType } from "antd";
import { businessTypeApi } from "api/business-type.api";
import { AppButton, AppTable, AppTableToolbar } from "components/antd-ui";
import { debounce } from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import { ModalStatus } from "types/modal";
import { QueryParam } from "types/query";
import { BusinessType } from "types/business-type";
import { $url } from "utils/url";
import {
  ActionButton,
  INA,
  TableLayout,
  TableLayoutFooter,
  getTableShowingRange,
} from "components/table";
import { getTitle } from "utils";
import { BusinessTypeModal } from "./components/BusinessTypeModal";

export const BusinessTypePage = ({ title = "" }) => {
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState<QueryParam>({
    page: 1,
    limit: 10,
    search: "",
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<BusinessType[]>([]);
  const [total, setTotal] = useState(0);
  const [visibleModal, setVisibleModal] = useState(false);
  const [selectedBusinessType, setSelectedBusinessType] = useState<
    Partial<BusinessType>
  >({});
  const [modalStatus, setModalStatus] = useState<ModalStatus>("create");

  const debounceSearch = useCallback(
    debounce(
      (keyword: string) =>
        setQuery({ ...query, search: keyword, page: 1, limit: 20 }),
      300,
    ),
    [setQuery, query],
  );

  useEffect(() => {
    document.title = getTitle(title);
  }, []);

  useEffect(() => {
    fetchData();
  }, [query]);

  const fetchData = async () => {
    setLoading(true);
    const res = await businessTypeApi.findAll(query);
    setLoading(false);
    setData(res.data.businessTypes);
    setTotal(res.data.total);
  };

  const handleDelete = async (row: BusinessType) => {
    setLoading(true);
    try {
      await businessTypeApi.delete(row.id);
      message.success("Xóa thành công");
      fetchData();
    } finally {
      setLoading(false);
    }
  };

  const updateData = async (row: BusinessType) => {
    const data = { businessType: { isPriority: !row.isPriority } };
    setLoading(true);
    try {
      await businessTypeApi.update(row?.id, data);
      message.success("Cập nhật thành công");
      fetchData();
    } finally {
      setLoading(false);
    }
  };

  const columns: TableColumnsType<BusinessType> = [
    {
      title: "STT",
      key: "stt",
      width: 80,
      render: (_, __, index) => <span>{(query.page - 1) * query.limit + index + 1}</span>,
    },
    {
      title: "Tên",
      dataIndex: "name",
      key: "name",
      render: (_, record) => (
        <INA
          image={record.image ? $url(record.image) : undefined}
          name={record.name}
          additional={record.type || "--"}
        />
      ),
    },
    {
      title: "Nổi bật",
      dataIndex: "isPriority",
      key: "isPriority",
      width: 110,
      align: "center",
      render: (_, record) => (
        <Tooltip title={record.isPriority ? "Bỏ nổi bật" : "Đánh dấu nổi bật"}>
          <ActionButton
            aria-label={record.isPriority ? "Bỏ nổi bật" : "Đánh dấu nổi bật"}
            onClick={() => updateData(record)}
          >
            {record.isPriority ? (
              <StarFilled style={{ color: "#f2d527" }} />
            ) : (
              <StarOutlined style={{ color: "#94a3b8" }} />
            )}
          </ActionButton>
        </Tooltip>
      ),
    },
    {
      title: "Thao tác",
      key: "action",
      width: 120,
      align: "center",
      render: (_, record) => (
        <div className="flex gap-2 justify-center items-center">
          <Tooltip title="Cập nhật">
            <ActionButton
              aria-label="Cập nhật"
              onClick={() => {
                setSelectedBusinessType(record);
                setVisibleModal(true);
                setModalStatus("update");
              }}
            >
              <EditOutlined style={{ color: "#6c84e5" }} />
            </ActionButton>
          </Tooltip>
          <Tooltip title="Xóa">
            <Popconfirm
              onConfirm={() => handleDelete(record)}
              placement="top"
              title="Xác nhận xóa"
            >
              <ActionButton aria-label="Xóa">
                <DeleteOutlined style={{ color: "#ef4444" }} />
              </ActionButton>
            </Popconfirm>
          </Tooltip>
        </div>
      ),
    },
  ];

  const { showingFrom, showingTo } = getTableShowingRange(
    query.page,
    query.limit,
    total,
  );

  return (
    <div>
      <TableLayout
        filter={
          <AppTableToolbar
            search={{
              value: search,
              placeholder: "Tìm kiếm",
              onChange: (value) => {
                setSearch(value);
                debounceSearch(value);
              },
              inputProps: {
                allowClear: {
                  clearIcon: (
                    <CloseCircleFilled
                      onClick={() => {
                        setSearch("");
                        setQuery((prev) => ({ ...prev, page: 1, search: "" }));
                      }}
                    />
                  ),
                },
              },
            }}
            leftActions={
              <AppButton
                tone="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setVisibleModal(true);
                  setModalStatus("create");
                  setSelectedBusinessType({});
                }}
              >
                Thêm mới
              </AppButton>
            }
            filterLabel={null}
          />
        }
        table={
          <AppTable<BusinessType>
            rowKey="id"
            loading={loading}
            pagination={false}
            dataSource={data}
            columns={columns}
            scroll={{ x: 760 }}
          />
        }
        footer={
          <TableLayoutFooter
            showingFrom={showingFrom}
            showingTo={showingTo}
            total={total}
            currentPage={query.page}
            pageSize={query.limit}
            onChange={({ limit, page }) => {
              query.page = page;
              query.limit = limit;
              setQuery({ ...query });
            }}
          />
        }
      />

      <BusinessTypeModal
        onSubmitOk={fetchData}
        onClose={() => setVisibleModal(false)}
        visible={visibleModal}
        businessType={selectedBusinessType}
        status={modalStatus}
      />
    </div>
  );
};
